"use client";

import React, { useState, useMemo } from 'react';
import {
  Search,
  Filter,
  Plus,
  ChevronDown,
  Tag,
  Percent,
  Calendar,
  Users,
  MoreVertical,
  Edit3,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  TrendingUp,
  LayoutList,
  Table2,
  X,
} from 'lucide-react';
import OfferModal from './OfferModal';
import { useOffers } from '../context/OfferContext';
import { useProducts } from '../context/ProductsContext';
import { useCategories } from '../context/CategoryContext';
import { useDiscountType } from "../context/DiscountTypeContext";

export default function OffersPage() {

  const noopAsync = async () => {};
  const noop = () => {};
  const { discountTypes, loading: discountLoading } = useDiscountType();
  const offersCtx = useOffers() || {
    offers: [],
    loading: false,
    fetchOffers: noopAsync,
    addOffer: noopAsync,
    updateOffer: noopAsync,
    deleteOffer: noopAsync,
    toggleStatus: noopAsync,
  };
  const { offers = [], loading = false, fetchOffers, addOffer, updateOffer, deleteOffer, toggleStatus } = offersCtx;

  const { products: allProducts, loading: productsLoading } = useProducts();
  const { categories: allCategories, loading: categoriesLoading } = useCategories();

  const categories = useMemo(() => allCategories || [], [allCategories]);
  const products = useMemo(() => allProducts || [], [allProducts]);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedOffers, setSelectedOffers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [toast, setToast] = useState(null);

const showToast = (message) => {
  setToast(message);
  setTimeout(() => setToast(null), 2500);
};

  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr.includes("-") && dateStr.split("-")[0].length === 2) {
      const [day, month, year] = dateStr.split("-");
      return new Date(`${year}-${month}-${day}`);
    }
    return new Date(dateStr);
  };

  const statusOptions = useMemo(() => {
    const now = new Date();
    const expiredCount = offers.filter(o => {
      if (!o.end_date) return false;
      return parseDate(o.end_date) < now;
    }).length;
    const activeCount = offers.filter(o => {
      if (!o.end_date) return false;
      const endDate = parseDate(o.end_date);
      return o.status?.toLowerCase() === "active" && endDate >= now;
    }).length;
    const inactiveCount = offers.filter(o => {
      if (!o.end_date) return false;
      const endDate = parseDate(o.end_date);
      return o.status?.toLowerCase() === "inactive" && endDate >= now;
    }).length;
    return [
      { value: "all", label: "All Offers", count: offers.length },
      { value: "active", label: "Active", count: activeCount },
      { value: "inactive", label: "Inactive", count: inactiveCount },
      { value: "expired", label: "Expired", count: expiredCount },
    ];
  }, [offers]);

  const typeOptions = useMemo(() => {
    if (!discountTypes) return [{ value: "all", label: "All Types" }];
    const activeTypes = discountTypes.filter(d => d.status?.toLowerCase() === "active");
    return [
      { value: "all", label: "All Types" },
      ...activeTypes.map(d => ({ value: String(d.id), label: d.name })),
    ];
  }, [discountTypes]);

  const getDiscountName = (id) => {
    const type = discountTypes?.find(d => String(d.id) === String(id));
    return type?.name || "Unknown";
  };

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'usage-high', label: 'Most Used' },
    { value: 'usage-low', label: 'Least Used' },
    { value: 'value-high', label: 'Highest Value' },
    { value: 'value-low', label: 'Lowest Value' },
    { value: 'expiry', label: 'Expiring Soon' },
  ];

  const filteredAndSortedOffers = useMemo(() => {
    const list = offers || [];
    const filtered = list.filter((offer) => {
      const matchesSearch =
        !searchTerm ||
        offer.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        offer.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        offer.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const now = new Date();
      const endDate = parseDate(offer.end_date);
      const isExpired = endDate && endDate < now;

      let matchesStatus = true;
      if (statusFilter === "active") matchesStatus = offer.status?.toLowerCase() === "active" && !isExpired;
      if (statusFilter === "inactive") matchesStatus = offer.status?.toLowerCase() === "inactive" && !isExpired;
      if (statusFilter === "expired") matchesStatus = isExpired;

      const matchesType = typeFilter === "all" || String(offer.discount_type_id) === String(typeFilter);
      return matchesSearch && matchesStatus && matchesType;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "newest": return new Date(b.create_audit_time) - new Date(a.create_audit_time);
        case "oldest": return new Date(a.create_audit_time) - new Date(b.create_audit_time);
        case "usage-high": return (b.used_count || 0) - (a.used_count || 0);
        case "usage-low": return (a.used_count || 0) - (b.used_count || 0);
        case "value-high": return Number(b.value || 0) - Number(a.value || 0);
        case "value-low": return Number(a.value || 0) - Number(b.value || 0);
        case "expiry": return new Date(a.end_date) - new Date(b.end_date);
        default: return 0;
      }
    });

    return filtered;
  }, [offers, searchTerm, statusFilter, typeFilter, sortBy]);

  const stats = useMemo(() => {
    const list = offers || [];
    const now = new Date();
    const totalOffers = list.length;
    const activeOffers = list.filter(o => {
      const endDate = parseDate(o.end_date);
      return o.status?.toLowerCase() === "active" && endDate >= now;
    }).length;
    const totalUsage = list.reduce((sum, o) => sum + (o.used_count || 0), 0);
    const expiringOffers = list.filter(o => {
      const endDate = parseDate(o.end_date);
      const daysUntilExpiry = (endDate - now) / (1000 * 60 * 60 * 24);
      return daysUntilExpiry <= 7 && daysUntilExpiry > 0;
    }).length;
    return { totalOffers, activeOffers, totalUsage, expiringOffers };
  }, [offers]);

  const handleSelectOffer = (offerId) => {
    setSelectedOffers(prev => prev.includes(offerId) ? prev.filter(id => id !== offerId) : [...prev, offerId]);
  };

  const handleSelectAll = () => {
    if (selectedOffers.length === filteredAndSortedOffers.length) {
      setSelectedOffers([]);
    } else {
      setSelectedOffers(filteredAndSortedOffers.map(o => o.id));
    }
  };

  const handleDeleteOffer = async (offerId) => {
    if (typeof window !== 'undefined' && !window.confirm('Are you sure you want to delete this offer?')) return;
    setActionLoading(true);
    try {
      await deleteOffer(offerId);
    } catch (err) {
      console.error(err);
      if (typeof window !== 'undefined') window.alert(err.message || 'Failed to delete offer');
    } finally {
      setActionLoading(false);
      setActiveDropdown(null);
      setSelectedOffers(prev => prev.filter(id => id !== offerId));
    }
  };

  const handleEditOffer = (offer) => {
    setEditingOffer(offer);
    setShowAddModal(true);
    setActiveDropdown(null);
  };

  const handleToggleStatus = async (offerId, currentStatus) => {
    setActionLoading(true);
    try {
     await toggleStatus(offerId, currentStatus);
    } catch (err) {
      console.error(err);
      if (typeof window !== 'undefined') window.alert(err.message || 'Failed to toggle status');
    } finally {
      setActionLoading(false);
      setActiveDropdown(null);
    }
  };

const handleCopyCode = (code) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    navigator.clipboard.writeText(code);
  }
  setActiveDropdown(null);
  showToast(`"${code}" copied to clipboard!`);
};

 const normalizeOfferPayload = (data) => ({
  code: data.code,
  title: data.title,
  description: data.description,

  discount_type_id: Number(data.discount_type_id),

  value: data.value,
  max_discount: data.maxDiscount || null,
  minimum_order_value: data.minOrderValue || null,

  user_restrictions: data.isFirstTimeUser ? "first_time_user" : "all",

  total_usage_limit: data.usageLimit || null,
  per_user_limit: data.userLimit || null,

  start_date: data.startDate,
  end_date: data.endDate,

  status: data.status || "Active",

  category_id: data.applicableCategories || [],   // ARRAY
  product_id: data.applicableProducts || [],      // ARRAY

  create_audit_id: localStorage.getItem("admin_id") || 1
});

const handleSaveOffer = async (offerData) => {
  setActionLoading(true);

  try {
    const payload = normalizeOfferPayload(offerData);

    if (editingOffer) {
      await updateOffer(editingOffer.id, payload);
    } else {
      await addOffer(payload);
    }

    setShowAddModal(false);
    setEditingOffer(null);

  } catch (err) {
    console.error(err);
    alert("Failed to save offer");
  } finally {
    setActionLoading(false);
  }
};

  const handleAddOffer = () => {
    setEditingOffer(null);
    setShowAddModal(true);
  };

  const getOfferTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'percentage': return <Percent className="w-4 h-4" />;
      case 'fixed': return <Tag className="w-4 h-4" />;
      case 'free_shipping': return <TrendingUp className="w-4 h-4" />;
      default: return <Tag className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'expired': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isOfferExpiring = (endDate) => {
    const end = parseDate(endDate);
    if (!end) return false;
    const now = new Date();
    const daysUntilExpiry = (end - now) / (1000 * 60 * 60 * 24);
    return daysUntilExpiry <= 7 && daysUntilExpiry > 0;
  };

  /* ─── Offer Card (List View) ─── */
  const OfferItem = ({ offer }) => {
    const isSelected = selectedOffers.includes(offer.id);
    const isExpiring = isOfferExpiring(offer.end_date);
    const discountName = getDiscountName(offer.discount_type_id);

    return (
      <div className={`bg-white bg-opacity-80 rounded-xl border transition-all duration-300 ${
        isSelected ? 'border-teal-400 shadow-lg' : 'border-orange-200 hover:shadow-lg hover:shadow-orange-200/50'
      }`}>
        <div className="p-4 sm:p-6">
          {/* Card Header */}
          <div className="flex items-start gap-3">
            {/* Checkbox */}
            {/* <input
              type="checkbox"
              checked={isSelected}
              onChange={() => handleSelectOffer(offer.id)}
              className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 mt-1 shrink-0"
              aria-label={`Select offer ${offer.code}`}
            /> */}

            {/* Content */}
            <div className="flex-1 min-w-0">
              {/* Title row */}
              <div className="flex flex-wrap items-start gap-2 mb-3">
                <h3 className="text-base sm:text-lg font-semibold text-teal-800 leading-tight">{offer.title}</h3>
                {isExpiring && (
                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full whitespace-nowrap shrink-0">
                    Expiring Soon
                  </span>
                )}
              </div>

              {/* Badge row */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-teal-50 rounded-lg min-w-0">
                  {getOfferTypeIcon(discountName)}
                  <span className="font-mono text-sm sm:text-base font-bold text-teal-800 truncate">{offer.code}</span>
                </div>
                <div className="px-2.5 py-1 bg-purple-50 text-purple-800 rounded-lg font-semibold text-sm whitespace-nowrap">
                  {offer.value} {discountName}
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(offer.status)}`}>
                  {offer.status}
                </span>
              </div>

              {/* Description */}
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">{offer.description}</p>

              {/* Stats grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <span className="text-gray-500 text-xs block mb-0.5">Usage</span>
                  <div className="font-medium text-gray-800">
                    {offer.used_count}{offer.total_usage_limit ? `/${offer.total_usage_limit}` : ''}
                  </div>
                  {offer.total_usage_limit && (
                    <div className="w-full mt-1">
                      <div
                        className="bg-teal-600 h-1.5 rounded-full"
                        style={{ width: `${Math.min((offer.used_count / offer.total_usage_limit) * 100, 100)}%` }}
                      />
                    </div>
                  )}
                </div>
                <div>
                  <span className="text-gray-500 text-xs block mb-0.5">Min Order</span>
                  <div className="font-medium text-gray-800">{offer.minimum_order_value ? `₹${offer.minimum_order_value}` : 'No minimum'}</div>
                </div>
                <div>
                  <span className="text-gray-500 text-xs block mb-0.5">Valid Until</span>
                  <div className="font-medium text-gray-800">{offer.end_date || '—'}</div>
                </div>
                <div>
                  <span className="text-gray-500 text-xs block mb-0.5">User Limit</span>
                  <div className="font-medium text-gray-800">{offer.per_user_limit ? `${offer.per_user_limit}/user` : 'Unlimited'}</div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
                <button
                  onClick={() => handleEditOffer(offer)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors text-xs sm:text-sm font-medium"
                >
                  <Edit3 className="w-3.5 h-3.5" />
                  {/* <span>Edit</span> */}
                </button>
                <button
                  onClick={() => handleCopyCode(offer.code)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors text-xs sm:text-sm font-medium"
                >
                  <Copy className="w-3.5 h-3.5" />
                  {/* <span>Copy</span> */}
                </button>
                <button
           onClick={() => handleToggleStatus(offer.id, offer.status)}
                  disabled={actionLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors text-xs sm:text-sm font-medium disabled:opacity-50"
                >
                  {offer.status === "Inactive" ? <><EyeOff className="w-3.5 h-3.5" /></> : <><Eye className="w-3.5 h-3.5" /></>}
                </button>
                <button
                  onClick={() => handleDeleteOffer(offer.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-xs sm:text-sm font-medium"
                  disabled={actionLoading}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {/* <span>Delete</span> */}
                </button>
              </div>
            </div>

            {/* Kebab menu */}
            {/* <div className="relative shrink-0">
              <button
                onClick={() => setActiveDropdown(activeDropdown === offer.id ? null : offer.id)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Offer actions"
              >
                <MoreVertical className="w-4 h-4 text-gray-600" />
              </button>

              {activeDropdown === offer.id && (
                <div className="absolute right-0 mt-2 w-44 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="py-1">
                    <button onClick={() => handleEditOffer(offer)} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                      <Edit3 className="w-4 h-4" />Edit Coupon
                    </button>
                    <button onClick={() => handleCopyCode(offer.code)} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                      <Copy className="w-4 h-4" />Copy Coupon
                    </button>
                    <button
                      onClick={() => handleToggleStatus(offer.id, offer.status)}
                      disabled={actionLoading}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      {offer.status === "Inactive" ? <><EyeOff className="w-4 h-4" />Inactive</> : <><Eye className="w-4 h-4" />Active</>}
                    </button>
                    <button onClick={() => handleDeleteOffer(offer.id)} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                      <Trash2 className="w-4 h-4" />Delete Offer
                    </button>
                  </div>
                </div>
              )}
            </div> */}
          </div>
        </div>
      </div>
    );
  };

  /* ─── Main Render ─── */
  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="max-w-7xl mx-auto">

        {/* ── Page Header ── */}
        <header className="flex flex-col sm:flex-row gap-4 sm:gap-5 justify-between items-start sm:items-center mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center">
              <Tag className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-teal-800">Coupons</h1>
              <p className="text-gray-600 mt-1">Manage discount codes and promotional Coupons</p>
            </div>
          </div>
          <button
            onClick={handleAddOffer}
            className="flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-xl hover:bg-teal-700 transition-colors font-medium"
          >
            <Plus className="w-5 h-5" />
            Create Coupon
          </button>
        </header>

        {/* ── Stats Cards ── */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
          {[
            { label: 'Total Coupons', value: stats.totalOffers, color: 'teal', Icon: Tag },
            { label: 'Active Coupons', value: stats.activeOffers, color: 'green', Icon: Eye },
            { label: 'Total Usage', value: stats.totalUsage.toLocaleString(), color: 'purple', Icon: Users },
            { label: 'Expiring Soon', value: stats.expiringOffers, color: 'orange', Icon: Calendar },
          ].map(({ label, value, color, Icon }) => (
            <div
              key={label}
              className="bg-white bg-opacity-80 rounded-xl border border-orange-200 p-4 sm:p-5 md:p-6 hover:shadow-lg transition-all"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-gray-600 mb-1 truncate">{label}</p>
                  <p className={`text-xl sm:text-2xl font-bold text-${color}-${color === 'teal' ? '800' : '600'}`}>{value}</p>
                </div>
                <div className={`w-9 h-9 sm:w-11 sm:h-11 md:w-12 md:h-12 bg-${color}-100 rounded-lg flex items-center justify-center shrink-0`}>
                  <Icon className={`w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-${color}-600`} />
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* ── Search & Filters ── */}
        <section className="bg-white bg-opacity-60 rounded-xl border border-orange-200 p-4 sm:p-6 mb-6 sm:mb-8 hover:shadow-2xl hover:shadow-orange-200/50 transition-all duration-300">
          {/* Search bar + filter toggle */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              <input
                type="text"
                placeholder="Search by code, title, or description…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 text-sm"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 text-teal-600 border border-teal-200 rounded-lg hover:bg-teal-50 hover:text-teal-800 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 text-sm font-medium whitespace-nowrap"
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Expandable filter panel */}
          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 text-sm"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label} ({option.count})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">Type</label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 text-sm"
                >
                  {typeOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 text-sm"
                >
                  {sortOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              {selectedOffers.length > 0 && (
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                    Bulk Actions ({selectedOffers.length} selected)
                  </label>
                  <div className="flex gap-2">
                    <button className="flex-1 px-2 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-xs sm:text-sm font-medium">
                      Activate
                    </button>
                    <button className="flex-1 px-2 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-xs sm:text-sm font-medium">
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* ── Offers List ── */}
      {/* ── Offers List ── */}
<section>
  <div className="flex items-center justify-between mb-4 sm:mb-6 gap-3">
    <span className="text-xs sm:text-sm text-gray-600">
      Showing <span className="font-semibold text-teal-700">{filteredAndSortedOffers.length}</span> of{' '}
      <span className="font-semibold">{offers.length}</span> Coupons
    </span>
    <div className="flex items-center border border-orange-200 rounded-lg overflow-hidden shrink-0">
      <button
        onClick={() => setViewMode('list')}
        className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-2 text-xs sm:text-sm font-medium transition-colors ${
          viewMode === 'list' ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-50'
        }`}
      >
        <LayoutList className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        <span className="hidden xs:inline">List</span>
      </button>
      <button
        onClick={() => setViewMode('table')}
        className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-2 text-xs sm:text-sm font-medium transition-colors ${
          viewMode === 'table' ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-50'
        }`}
      >
        <Table2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        <span className="hidden xs:inline">Table</span>
      </button>
    </div>
  </div>

  {loading ? (
    <div className="text-center py-16 text-gray-500">
      <div className="w-10 h-10 border-2 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
      Loading Coupons…
    </div>
  ) : filteredAndSortedOffers.length === 0 ? (
    <div className="text-center py-16 text-gray-500">
      <Tag className="w-12 h-12 mx-auto mb-3 text-gray-300" />
      <p className="font-medium">No coupons found</p>
      <p className="text-sm mt-1">Try adjusting your search or filters</p>
    </div>
  ) : viewMode === 'table' ? (

    /* ── Table View ── */
    <div className="bg-white bg-opacity-80 rounded-xl border border-orange-200 overflow-hidden w-full">
      {/* KEY FIX: -mx-3 sm:-mx-4 md:mx-0 pulls table edge-to-edge on small screens */}
      <div className="overflow-x-auto w-full">
        <table className="w-full text-xs" style={{ minWidth: '560px' }}>
          <thead>
            <tr className="bg-teal-50 border-b border-orange-200">
              <th className="px-2 py-2.5 text-left font-semibold text-teal-800 w-7">#</th>
              <th className="px-2 py-2.5 text-left font-semibold text-teal-800">Code</th>
              <th className="px-2 py-2.5 text-left font-semibold text-teal-800">Title</th>
              {/* Type — only lg+ */}
              <th className="px-2 py-2.5 text-left font-semibold text-teal-800 hidden lg:table-cell">Type</th>
              <th className="px-2 py-2.5 text-left font-semibold text-teal-800">Value</th>
              {/* Usage — sm+ */}
              <th className="px-2 py-2.5 text-left font-semibold text-teal-800 hidden sm:table-cell w-14">Usage</th>
              {/* Valid Until — md+ (covers 768px+, safe for 820px) */}
              <th className="px-2 py-2.5 text-left font-semibold text-teal-800 hidden md:table-cell whitespace-nowrap">Valid Until</th>
              <th className="px-2 py-2.5 text-left font-semibold text-teal-800">Status</th>
              <th className="px-2 py-2.5 text-center font-semibold text-teal-800">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedOffers.map((offer, index) => (
              <tr key={offer.id} className="border-b border-gray-100 hover:bg-orange-50/40 transition-colors">
                <td className="px-2 py-2.5 text-gray-400 font-medium">{index + 1}</td>

                {/* Code */}
                <td className="px-2 py-2.5">
                  <span className="font-mono font-bold text-teal-800 bg-teal-50 px-1.5 py-0.5 rounded whitespace-nowrap text-[11px]">
                    {offer.code}
                  </span>
                </td>

                {/* Title — controlled truncation per breakpoint */}
                <td className="px-2 py-2.5 font-medium text-gray-800" style={{ maxWidth: '90px' }}>
                  <div className="truncate">{offer.title}</div>
                </td>

                {/* Type — lg+ only */}
                <td className="px-2 py-2.5 hidden lg:table-cell">
                  <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded-full font-medium whitespace-nowrap">
                    {getDiscountName(offer.discount_type_id)}
                  </span>
                </td>

                {/* Value */}
                <td className="px-2 py-2.5 font-semibold text-purple-700 whitespace-nowrap">{offer.value}</td>

                {/* Usage — sm+ */}
                <td className="px-2 py-2.5 text-gray-600 hidden sm:table-cell whitespace-nowrap">
                  {offer.used_count}{offer.total_usage_limit ? `/${offer.total_usage_limit}` : ''}
                </td>

                {/* Valid Until — md+ */}
                <td className="px-2 py-2.5 text-gray-500 whitespace-nowrap hidden md:table-cell">
                  {offer.end_date || '—'}
                </td>

                {/* Status toggle */}
                <td className="px-2 py-2.5">
                  <button
                    onClick={() => handleToggleStatus(offer.id, offer.status)}
                    title={offer.status === 'Active' ? 'Click to Deactivate' : 'Click to Activate'}
                    className={`relative inline-flex items-center w-9 h-5 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-teal-500 ${
                      offer.status === 'Active' ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block w-3.5 h-3.5 bg-white rounded-full shadow transform transition-transform duration-200 ${
                      offer.status === 'Active' ? 'translate-x-[18px]' : 'translate-x-0.5'
                    }`} />
                  </button>
                </td>

                {/* Actions */}
                <td className="px-2 py-2.5">
                  <div className="flex items-center justify-center gap-0.5">
                    <button
                      onClick={() => handleEditOffer(offer)}
                      className="p-1 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleCopyCode(offer.code)}
                      className="p-1 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                      title="Copy Code"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeleteOffer(offer.id)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-3 py-2.5 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
        <span>{filteredAndSortedOffers.length} records</span>
        <span>Showing {filteredAndSortedOffers.length} of {offers.length} Coupons</span>
      </div>
    </div>

  ) : (
    /* ── List View ── */
    <div className="space-y-3 sm:space-y-4">
      {filteredAndSortedOffers.map((offer) => (
        <OfferItem key={offer.id} offer={offer} />
      ))}
    </div>
  )}
</section>
      </div>

      {/* Offer Modal */}
      <OfferModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingOffer(null);
        }}
        offer={editingOffer}
        products={products}
        categories={categories}
        onSave={handleSaveOffer}
        loading={actionLoading}
      />

      {/* Backdrop to close dropdowns */}
      {activeDropdown && (
        <div className="fixed inset-0 z-40" onClick={() => setActiveDropdown(null)} />
      )}
      {/* Toast Notification */}
{toast && (
  <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-2.5 px-5 py-3 bg-gray-900 text-white text-sm font-medium rounded-xl shadow-xl animate-fade-in-up">
    <Copy className="w-4 h-4 text-teal-400 shrink-0" />
    {toast}
  </div>
)}
    </div>
  );
}